import { themeColors } from "../../../themeColors";
/**
 * @file   src\components\MemberWantAd\EditProfileModal.js
 * @brief  This file is responsible for editing agent profile.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Row, Col, Button } from "react-bootstrap";
import TextBox from "../../Common/TextBox";
import TextArea from "../../Common/TextArea";
import clsx from "clsx";
import {
  toTitleCase,
  getFileType,
  upperCaseEachSentenceFirst,
  getLocalStorage,
  setLocalStorage,
  trimString,
  formatPhoneNumber,
  removeLocalStorage,
} from "../../../helpers/common";
import {
  nameValidation,
  alphaNumericValidation,
  textValidations,
  licenseValidation,
  phoneNumberValidation,
} from "../../../helpers/validations";
import { editProfilePremiumAgent } from "../../../services/userprofileService";
import { toast } from "react-toastify";
import Loader from "../../Common/Loader";
import CheckBox from "../../Common/CheckBox";
import {
  AGENT_USER_FREE_PLAN,
  BIO_MAX_COUNT,
  TESTIMONIAL_MAX_COUNT,
  BOTH_TITLE,
  PROPERTY_MANAGER,
  REAL_ESTATE_AGENT,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
} from "../../../constants/common";
import { useNavigate } from "react-router-dom";
import PlusIcon from "../../../images/v2/plus-icon.svg";
const supportedImageFormats = ["jpg", "jpeg", "png"];
import { getGeoLocation } from "../../../services/commonServices";
import { capitalizeString } from "../../../actions/common";
import useIsMobile from "@/hooks/useIsMobile";

const EditProfileModal = (props) => {
  const { userDetails, editAgentProfile, isFromUpgradeToAgent, selectedPlan } =
    props;
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(userDetails?.FirstName);
  const [lastName, setLastName] = useState(userDetails?.LastName);
  const [company, setCompany] = useState(userDetails?.Company);
  const [location, setLocation] = useState(userDetails?.Location);
  const [locationError, setLocationError] = useState("");
  const [bio, setBio] = useState(userDetails?.Bio);
  const [imgError, setImgError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [bioCount, setBioCount] = useState(0);
  const [testimonialCounts, setTestimonialCounts] = useState([0]);
  const [imageUrl, setImageUrl] = useState(userDetails?.Img);
  const [clientTestimonial, setTestimonials] = useState([]);
  const [logoUrl, setLogoUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [bioError, setBioError] = useState("");
  const locationString = getLocalStorage("LocationString");
  const [locationList, setLocationList] = useState([]);
  const [formattedAddress, setFormattedAddress] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [license, setLicense] = useState(userDetails?.LicenseNo);
  const [licenseError, setLicenseError] = useState("");
  const [websiteUrlError, setWebsiteUrlError] = useState(false);
  const [youtubeUrlError, setYoutubeUrlError] = useState(false);
  const [facebookUrlError, setFacebookUrlError] = useState(false);
  const [instagramUrlError, setInstagramUrlError] = useState(false);
  const [isRealEstateAgent, setIsRealEstateAgent] = useState(
    userDetails?.Agent_Title === REAL_ESTATE_AGENT ||
      userDetails?.Agent_Title === BOTH_TITLE
      ? true
      : false,
  );
  const [isPropertyManager, setIsPropertyManager] = useState(
    userDetails?.Agent_Title === PROPERTY_MANAGER ||
      userDetails?.Agent_Title === BOTH_TITLE
      ? true
      : false,
  );

  const [bioCursorPos, setBioCursorPos] = useState(0);
  const bioRef = useRef();

  const [phone, setPhone] = useState(userDetails?.Phone);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    setPhone(userDetails?.Phone);
    setFirstName(userDetails?.FirstName);
    setLastName(userDetails?.LastName);
    setCompany(userDetails?.Company);
    setLocation(userDetails?.Location);
    setBio(userDetails?.Bio);
    setLicense(userDetails?.LicenseNo);
    setTestimonials(
      userDetails?.clientTestimonial == null
        ? [""]
        : userDetails?.clientTestimonial,
    );
    setFacebookUrl(userDetails?.facebookUrl);
    setInstagramUrl(userDetails?.instagramUrl);
    setWebsiteUrl(userDetails?.websiteUrl);
    setYoutubeUrl(userDetails?.youtubeUrl);
  }, [userDetails]);

  useEffect(() => {
    setImageUrl(
      props?.uploadedImageUrl ? props?.uploadedImageUrl : userDetails?.Img,
    );
  }, [props?.uploadedImageUrl, props?.reRenderEditProfileModel]);

  useEffect(() => {
    setLogoUrl(
      props?.uploadedLogoUrl ? props?.uploadedLogoUrl : userDetails?.logoUrl,
    );
  }, [props?.uploadedLogoUrl, props?.reRenderEditProfileModel]);

  const getAddresseDetails = async ({ eventType, reqPayload }) => {
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (results?.length > 0 && formatted_address !== "United States") {
          if (locationString) {
            handleItemSelection(results[0]);
            removeLocalStorage("LocationString");
          } else {
            setLocationList(results);
          }
        }
      }
    });
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");
    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }
    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }
    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });

    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }
    if (city != undefined) {
      setFormattedAddress(city + ", " + state);
    } else {
      setFormattedAddress(formatted_address);
    }
    setLocation(city != undefined ? city + ", " + state : formatted_address);
    clearAddressList();
  };

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "") {
      getAddresseDetails({ reqPayload: reqPayload });
    } else {
      clearAddressList();
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      setLocation(value);
    }
  };

  const clearAddressList = () => {
    setLocationList([]);
  };

  /**
   * To handle first name field changes
   * @param {*} name
   */
  const handleFirstName = (name) => {
    setFirstNameError("");
    setFirstName(name);
    if (name.trim() === "") {
      setFirstNameError("Enter First Name");
    } else if (!nameValidation(name.trim())) {
      setFirstNameError("Invalid input");
    }
  };

  /**
   * To handle last name field changes
   * @param {*} name
   */
  const handleLastName = (name) => {
    setLastNameError("");
    setLastName(name);
    if (name.trim() === "") {
      setLastNameError("Enter Last Name");
    } else if (!nameValidation(name.trim())) {
      setLastNameError("Invalid input");
    }
  };

  /**
   * To handle company field changes
   * @param {*} name
   */
  const handleCompany = (name) => {
    setCompanyError("");
    setCompany(name);
    if (name.trim() === "") {
      setCompanyError("Enter Company");
    } else if (
      !/^[a-zA-Z0-9\s&]+$/.test(name.trim()) ||
      /^[0-9&]+$/.test(name.trim())
    ) {
      setCompanyError("Invalid input");
    }
  };

  useEffect(() => {
    if (bioRef.current) {
      bioRef.current.setSelectionRange(bioCursorPos, bioCursorPos);
    }
  }, [bioCursorPos]);
  /**
   * To handle  bio changes
   * @param {*} e
   */
  const handleBio = (e) => {
    let text = e.target.value;
    text = upperCaseEachSentenceFirst(text);
    setBioError("");
    if (trimString(text) != "") {
      setBioCursorPos(e.target.selectionStart);
    }
    setBio(text);
    setBioCount(text.length);
  };

  const handleTestimonial = (e, index) => {
    const value = e.target.value;
    setTestimonials((prevTestimonials) =>
      prevTestimonials.map((testimonial, i) =>
        i === index ? value : testimonial,
      ),
    );
    setTestimonialCounts((prevCounts) =>
      prevCounts.map((count, i) => (i === index ? value.length : count)),
    );
  };

  const addTestimonial = () => {
    setTestimonials((prevTestimonials) => [...prevTestimonials, ""]);
    setTestimonialCounts((prevCounts) => [...prevCounts, 0]);
  };

  /**
   * To handle  location field changes
   * @param {*} name
   */

  /**
   * To handle license field changes
   * @param {*} name
   */
  const handleLicenseChange = (name) => {
    setLicenseError("");
    setLicense(name);
    if (name.trim() === "") {
      setLicenseError("Enter License Number");
    } else if (!licenseValidation(name.trim())) {
      //V2 change for license validation
      setLicenseError("Invalid input");
    }
  };

  /**
   * To handle phone number field changes
   * @param {*} value
   */

  const handlePhone = (value) => {
    setPhoneError("");
    if (trimString(value) !== "") {
      value = formatPhoneNumber(value);
      setPhone(value);
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  /**
   * To validate phone number
   * @param {*} name
   */
  const validatePhoneNumber = () => {
    setPhoneError("");
    if (trimString(phone) !== "") {
      if (!phoneNumberValidation(phone.trim()) || phone === "000-000-0000") {
        setPhoneError("Enter Valid Phone Number");
      }
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  /**
   * To handle image upload
   * @param {*} e
   */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const selectedFileType = getFileType(_.get(e.target.files[0], "name"));
    let isValid = true;
    if (!_.includes(supportedImageFormats, selectedFileType)) {
      isValid = false;
      setImgError("Select Valid Image");
    }
    if (isValid) {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          props.onClickChangePhoto(reader.result);
        };
        reader.readAsDataURL(file);
        // Reset the file input
        e.target.value = null;
      }
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    const selectedFileType = getFileType(_.get(e.target.files[0], "name"));
    let isValid = true;
    setLogoError("");
    if (!_.includes(supportedImageFormats, selectedFileType)) {
      isValid = false;
      setLogoError("Select Valid Image");
    }
    if (isValid) {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          props.onClickChangePhoto(reader.result, true);
        };
        reader.readAsDataURL(file);
        // Reset the file input
        e.target.value = null;
      }
    }
  };

  const handleCancel = () => {
    setFirstName(userDetails?.FirstName);
    setLastName(userDetails?.LastName);
    setCompany(userDetails?.Company);
    setLicense(userDetails?.LicenseNo);
    setLocation(userDetails?.Location);
    setBio(userDetails?.Bio);
    setImageUrl(userDetails?.Img);
    setTestimonials(userDetails?.clientTestimonial);
    setFacebookUrl(userDetails?.facebookUrl);
    setInstagramUrl(userDetails?.instagramUrl);
    setWebsiteUrl(userDetails?.websiteUrl);
    setYoutubeUrl(userDetails?.youtubeUrl);
    setLogoUrl(userDetails?.logoUrl);
    setIsPropertyManager(
      userDetails?.Agent_Title === PROPERTY_MANAGER ||
        userDetails?.Agent_Title === BOTH_TITLE
        ? true
        : false,
    );
    setIsRealEstateAgent(
      userDetails?.Agent_Title === REAL_ESTATE_AGENT ||
        userDetails?.Agent_Title === BOTH_TITLE
        ? true
        : false,
    );
    setPhone(userDetails?.Phone);
    setPhoneError("");
    setLicenseError("");
    setBioError("");
    setFirstNameError("");
    setLastNameError("");
    setCompanyError("");
    setLocationError("");
    setLogoError("");
    props.onHide();
  };

  const getTitleOf = () => {
    if (isPropertyManager && isRealEstateAgent) {
      return 3;
    } else if (isPropertyManager) {
      return 2;
    } else {
      return 1;
    }
  };
  const handleUrlChange = (url, type) => {
    const trimmedUrl = url?.trim() || ""; // Ensure it's a string
    const websiteUrlRegex =
      /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;

    // Allow empty value, but validate only when there is input
    const isValid = trimmedUrl === "" || websiteUrlRegex.test(trimmedUrl);

    switch (type) {
      case "facebook":
        setFacebookUrl(trimmedUrl);
        setFacebookUrlError(trimmedUrl !== "" && !isValid); // Error only if input is invalid
        break;
      case "instagram":
        setInstagramUrl(trimmedUrl);
        setInstagramUrlError(trimmedUrl !== "" && !isValid);
        break;
      case "website":
        setWebsiteUrl(trimmedUrl);
        setWebsiteUrlError(trimmedUrl !== "" && !isValid);
        break;
      default:
        break;
    }
  };

  const handleYoutubeUrl = (url) => {
    const trimmedUrl = url.trim();
    setYoutubeUrl(trimmedUrl);
    setYoutubeUrlError(
      trimmedUrl === "" ? false : !validateYoutubeUrl(trimmedUrl),
    );
  };

  const validateYoutubeUrl = (url) => {
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)$/;
    return youtubeRegex.test(url);
  };

  /**
   * To handle submit changes
   */
  const handleSubmit = async () => {
    const data = {
      FirstName: firstName,
      LastName: lastName,
      Company: company,
      About: bio,
      Img: imageUrl,
      Title: getTitleOf(),
      Location: location,
      LicenseNo: license,
      Phone: phone,
      facebookUrl: facebookUrl,
      instagramUrl: instagramUrl,
      websiteUrl: websiteUrl,
      youtubeUrl: youtubeUrl,
      clientTestimonial: clientTestimonial,
      logoUrl: logoUrl,
    };
    await editProfilePremiumAgent(data)
      .then((response) => {
        if (response.errorCode == 0) {
          toast.success("User's profile updated successfully.");
          let userInfo = getLocalStorage(USER_INFO_KEY);
          userInfo.userInfo["firstName"] = firstName;
          userInfo.userInfo["lastName"] = lastName;
          userInfo.userInfo["name"] = firstName + " " + lastName;
          userInfo.userInfo["image"] = imageUrl;
          userInfo.userInfo["userType"] = USER_TYPE_AGENT;
          userInfo.userInfo["facebookUrl"] = facebookUrl;
          userInfo.userInfo["instagramUrl"] = instagramUrl;
          userInfo.userInfo["websiteUrl"] = websiteUrl;
          userInfo.userInfo["youtubeUrl"] = youtubeUrl;
          userInfo.userInfo["clientTestimonial"] = clientTestimonial;
          userInfo.userInfo["logoUrl"] = logoUrl;
          setLocalStorage(USER_INFO_KEY, userInfo);
          navigate("/settings");

          props.onHide();
        } else {
          toast.error(response.errorMessage);
        }
      })
      .catch((err) => {
        toast.error(err);
      });
  };
  /**
   * To handle validation
   * @returns
   */
  const checkValid = () => {
    if (
      userDetails?.PlanName === AGENT_USER_FREE_PLAN ||
      selectedPlan === "Lite"
    ) {
      if (
        location &&
        license &&
        company &&
        firstName &&
        lastName &&
        (isPropertyManager === true || isRealEstateAgent === true)
      ) {
        if (
          locationError ||
          licenseError ||
          companyError ||
          firstNameError ||
          lastNameError
        ) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    } else {
      if (!phoneNumberValidation(phone)) {
        return true;
      } else if (
        firstName === userDetails?.FirstName &&
        lastName === userDetails?.LastName &&
        bio === userDetails?.Bio &&
        phone === userDetails?.phone &&
        company === userDetails?.Company &&
        imageUrl === userDetails?.Img &&
        logoUrl === userDetails?.logoUrl &&
        license === userDetails?.LicenseNo &&
        location === userDetails?.Location &&
        (isPropertyManager ===
        (userDetails?.Agent_Title === PROPERTY_MANAGER ||
          userDetails?.Agent_Title === BOTH_TITLE)
          ? true
          : false) &&
        (isRealEstateAgent ===
        (userDetails?.Agent_Title === REAL_ESTATE_AGENT ||
          userDetails?.Agent_Title === BOTH_TITLE)
          ? true
          : false)
      ) {
        return true;
      } else if (
        firstNameError !== "" ||
        lastNameError !== "" ||
        companyError !== "" ||
        bioError !== "" ||
        phoneError !== "" ||
        imgError !== "" ||
        logoError !== "" ||
        locationError !== "" ||
        licenseError !== "" ||
        websiteUrlError ||
        youtubeUrlError ||
        facebookUrlError ||
        instagramUrlError
      ) {
        return true;
      } else if (
        firstName?.trim() === "" ||
        lastName?.trim() == "" ||
        company?.trim() == "" ||
        location?.trim() == "" ||
        (isPropertyManager === false && isRealEstateAgent === false)
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isNonPro =
    userDetails?.PlanName === AGENT_USER_FREE_PLAN || selectedPlan === "Lite";

  const isMobile = useIsMobile();

  return (
    <>
      {isUploading ? <Loader /> : ""}
      <Modal
        show
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="modal-settings"
        backdrop="static"
        fullscreen={isMobile}
      >
        <Modal.Body className="agent-info-modal">
          {editAgentProfile || isFromUpgradeToAgent ? (
            <></>
          ) : (
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleCancel}
            ></button>
          )}
          <h3 className="agent-modal-head mt-0">Edit profile</h3>

          <div className="profile-info mb-4">
            <div className="profile-pic-sec mb-3 mb-sm-0">
              <div className="prof-img mr-12 mr-sm-12 mb-3 mb-sm-0">
                {imageUrl != null && imageUrl != "" ? (
                  <img src={imageUrl + "?" + Math.random().toString(36)} />
                ) : (
                  <svg
                    width="24"
                    height="23"
                    viewBox="0 0 24 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M23.3643 21.1875C21.7317 18.375 19.1183 16.264 16.0253 15.2594C17.5078 14.3778 18.6599 13.0338 19.3046 11.4339C19.9493 9.83406 20.0508 8.06678 19.5937 6.40358C19.1365 4.74039 18.146 3.27329 16.7741 2.22766C15.4023 1.18204 13.7252 0.615723 12.0003 0.615723C10.2754 0.615723 8.59824 1.18204 7.22643 2.22766C5.85462 3.27329 4.86405 4.74039 4.4069 6.40358C3.94975 8.06678 4.05131 9.83406 4.69597 11.4339C5.34063 13.0338 6.49273 14.3778 7.97529 15.2594C4.88227 16.264 2.2689 18.375 0.636225 21.1875C0.556066 21.3194 0.513672 21.4707 0.513672 21.625C0.513672 21.7793 0.556066 21.9307 0.636225 22.0625C0.711012 22.1966 0.820597 22.308 0.953423 22.385C1.08625 22.462 1.23739 22.5018 1.39091 22.5H22.6097C22.7632 22.5018 22.9143 22.462 23.0472 22.385C23.18 22.308 23.2896 22.1966 23.3643 22.0625C23.4445 21.9307 23.4869 21.7793 23.4869 21.625C23.4869 21.4707 23.4445 21.3194 23.3643 21.1875Z"
                      fill={themeColors.neutral450}
                    />
                  </svg>
                )}
              </div>
              <div className="profile-name">
                <p className="title">
                  {userDetails?.FirstName} {userDetails?.LastName}
                </p>
                {userDetails?.Company && (
                  <p className="position">Agent @ {userDetails?.Company}</p>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center">
              <div className="upload-btn-innerwrap">
                <input
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  onChange={(e) => handleImageUpload(e)}
                  className=" "
                  disabled={userDetails?.period === 1}
                />
                <label
                  htmlFor="profileImage"
                  className={
                    isNonPro
                      ? "float-none float-md-end disabled-label"
                      : "active agent-user-btn"
                  }
                >
                  Change photo
                </label>
                <span className="error-msg w-104 h-auto error-wordbreak">
                  {imgError}
                </span>
              </div>
              {isNonPro && <div className="pro-badge ms-2">pro</div>}
            </div>
          </div>
          <Row>
            <Col md={6} className="pe-2">
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  id="fName"
                  label="First name*"
                  name="firstName"
                  type="text"
                  maxLength={30}
                  value={toTitleCase(firstName)}
                  placeholder="First name"
                  labelClassName="agent-modal-label"
                  onChange={(e) => handleFirstName(e.target.value)}
                  errorClass="error-validation-msg"
                  errorMessage={firstNameError}
                />
              </div>
            </Col>

            <Col md={6} className="ps-2">
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  id="lName"
                  name="lastName"
                  label="Last name*"
                  maxLength={30}
                  value={toTitleCase(lastName)}
                  type="text"
                  placeholder="Last name"
                  labelClassName="agent-modal-label"
                  errorClass="error-validation-msg"
                  onChange={(e) => handleLastName(e.target.value)}
                  errorMessage={lastNameError}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 d-inline-flex me-4">
                <label
                  className="form-check-label checkbox-label"
                  htmlFor="flexCheckDefault78"
                >
                  <CheckBox
                    className="form-check-input margin-top-zero"
                    type="checkbox"
                    id="realestate_agent"
                    checked={isRealEstateAgent}
                    onChange={(e) => setIsRealEstateAgent(!isRealEstateAgent)}
                  />{" "}
                  Real Estate Agent
                </label>
              </div>
              <div className="textbox-wrap mb-3 mb-sm-3 d-inline-flex">
                <label
                  className="form-check-label checkbox-label"
                  htmlFor="flexCheckDefault78"
                >
                  <CheckBox
                    className="form-check-input margin-top-zero"
                    type="checkbox"
                    id="property_manager"
                    checked={isPropertyManager}
                    onChange={(e) => setIsPropertyManager(!isPropertyManager)}
                  />{" "}
                  Property Manager
                </label>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  id="phone"
                  name="phone"
                  label={
                    <>
                      Phone Number{" "}
                      {isNonPro ? (
                        <div className="pro-badge d-inline-flex align-items-center ms-1">
                          pro
                        </div>
                      ) : (
                        <span>*</span>
                      )}
                    </>
                  }
                  labelClassName="agent-modal-label"
                  disabled={isNonPro}
                  type="text"
                  value={phone}
                  className={`${isNonPro ? "disabled-input" : ""}`}
                  errorClass="error-validation-msg"
                  onChange={(e) => handlePhone(e.target.value)}
                  onBlur={() => {
                    validatePhoneNumber();
                  }}
                  errorMessage={phoneError}
                  placeholder="Phone number"
                  maxLength={14}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  id="location"
                  name="location"
                  label="Location*"
                  type="text"
                  value={toTitleCase(location)}
                  labelClassName="agent-modal-label"
                  errorClass="error-validation-msg"
                  onChange={(e) => handleLocationChange(e.target.value)}
                  errorMessage={locationError}
                  placeholder="City, State"
                  maxLength={40}
                />
                <div className="error-msg"></div>
                {locationList.length > 0 ? (
                  <div className="location-result-wrap-new">
                    <ul>
                      {locationList?.map((address, i) => {
                        return (
                          <li key={i}>
                            <a
                              role="button"
                              href={void 0}
                              onClick={(e) => {
                                handleItemSelection(address);
                              }}
                            >
                              {_.get(address, "formatted_address", "")}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  id="company"
                  name="company"
                  label="Company*"
                  type="text"
                  value={toTitleCase(company)}
                  labelClassName="agent-modal-label"
                  errorClass="error-validation-msg"
                  onChange={(e) => handleCompany(e.target.value)}
                  errorMessage={companyError}
                  placeholder="Company name"
                  maxLength={40}
                />
              </div>
            </Col>
          </Row>
          <Row>
            {logoUrl && (
              <Col md={6} className="mb-3">
                <img
                  src={logoUrl + "?" + Math.random().toString(36)}
                  alt="Company Logo"
                  style={{ maxHeight: "100px", maxWidth: "150px" }}
                />
              </Col>
            )}

            <Col
              className={clsx("d-flex align-items-center ", {
                "justify-content-end": logoUrl,
              })}
            >
              <div className="upload-btn-innerwrap mb-3">
                <label
                  htmlFor="logoUrl"
                  className={`active agent-user-btn  ${isNonPro ? "disabled" : ""}`}
                >
                  {logoUrl ? "Change Logo" : "Add Logo"}
                </label>
                <input
                  type="file"
                  name="logoUrl"
                  id="logoUrl"
                  accept="image/*"
                  onChange={(e) => handleLogoUpload(e)}
                  className=" "
                  disabled={isNonPro}
                />
                {isNonPro && (
                  <div className="pro-badge d-inline-flex align-items-center ms-1">
                    pro
                  </div>
                )}
                <span className="error-msg error-wordbreak">{logoError}</span>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <label className="agent-modal-label">
                  License Number <span>*</span>
                </label>
                <TextBox
                  id="license"
                  name="license"
                  type="text"
                  value={license}
                  className="form-control"
                  errorClass="error-validation-msg"
                  onChange={(e) => handleLicenseChange(e.target.value)}
                  errorMessage={licenseError}
                  placeholder="License Number"
                  maxLength={40}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 position-relative agent-info-textfield">
                <TextArea
                  label={
                    <>
                      Bio (optional)
                      {isNonPro && (
                        <div className="pro-badge d-inline-flex align-items-center ms-1">
                          pro
                        </div>
                      )}
                    </>
                  }
                  labelClassName="agent-modal-label"
                  className="txt-area"
                  maxLength={BIO_MAX_COUNT}
                  value={bio}
                  errorMessage={bioError}
                  placeholder="More about you..."
                  errorClass="error-validation-msg"
                  onChange={(e) => handleBio(e)}
                  txtRef={bioRef}
                  disabled={isNonPro}
                />
                <div className="des-count mb-3">
                  <span>{bioCount}</span>/<span>{BIO_MAX_COUNT}</span>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              {Array.isArray(clientTestimonial) &&
                clientTestimonial.map((testimonial, index) => (
                  <div
                    className="textbox-wrap mb-3 mb-sm-3 position-relative agent-info-textfield"
                    key={index}
                  >
                    <TextArea
                      label="Client Testimonial (optional)"
                      labelClassName="agent-modal-label"
                      className="txt-area"
                      maxLength={1000}
                      value={testimonial}
                      placeholder="What your clients think..."
                      onChange={(e) => handleTestimonial(e, index)}
                      disabled={isNonPro}
                    />
                    <div className="des-count mb-2">
                      <span>{testimonialCounts[index]}</span>/
                      <span>{TESTIMONIAL_MAX_COUNT}</span>
                    </div>
                  </div>
                ))}

              <div
                onClick={addTestimonial}
                className="d-flex align-items-center add-testimonial-btn"
              >
                <img src={PlusIcon} alt="Add Icon" />
                <button className="add-another-testimonial">
                  Add another testimonial
                </button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  id="facebookUrl"
                  label="Facebook Profile (optional)"
                  name="facebookUrl"
                  type="text"
                  value={facebookUrl}
                  placeholder="Paste link to profile here"
                  labelClassName="agent-modal-label"
                  className={`${isNonPro ? "disabled-input" : ""}`}
                  onChange={(e) => handleUrlChange(e.target.value, "facebook")}
                  errorClass="error-validation-msg"
                  errorMessage={facebookUrlError ? "Enter Valid URl" : ""}
                  // errorMessage={firstNameError}
                />
              </div>
            </Col>

            <Col md={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  label="Instagram Profile (optional)"
                  id="instagramUrl"
                  name="instagramUrl"
                  value={instagramUrl}
                  type="text"
                  placeholder="Paste link to profile here"
                  labelClassName="agent-modal-label"
                  className={`${isNonPro ? "disabled-input" : ""}`}
                  errorClass="error-validation-msg"
                  onChange={(e) => handleUrlChange(e.target.value, "instagram")}
                  errorMessage={instagramUrlError ? "Enter Valid URl" : ""}
                  // errorMessage={lastNameError}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <TextBox
                  label="Website"
                  id="websiteUrl"
                  name="websiteUrl"
                  type="text"
                  value={websiteUrl}
                  placeholder="Paste link to website here"
                  labelClassName="agent-modal-label"
                  onChange={(e) => handleUrlChange(e.target.value, "website")}
                  errorClass="error-validation-msg"
                  errorMessage={websiteUrlError ? "Enter Valid URl" : ""}
                  // errorMessage={firstNameError}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="textbox-wrap mb-3 mb-sm-3 agent-info-textfield">
                <label className="agent-modal-label">Youtube Video</label>
                <TextBox
                  id="youtubeUrl"
                  name="youtubeUrl"
                  type="text"
                  value={youtubeUrl}
                  placeholder="Paste video embed link here"
                  className={`form-control ${isNonPro ? "disabled-input" : ""}`}
                  onChange={(e) => handleYoutubeUrl(e.target.value)}
                  errorClass="error-validation-msg"
                  errorMessage={
                    youtubeUrlError ? "Enter Valid Youtube URl" : ""
                  }
                  // errorMessage={firstNameError}
                />
              </div>
            </Col>
          </Row>

          <div className="modal-btn-wrap">
            {editAgentProfile || isFromUpgradeToAgent ? (
              <></>
            ) : (
              <Button
                variant=""
                className="btn btn-tertiary btn-margin-fix me-3"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              disabled={checkValid()}
              onClick={handleSubmit}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default EditProfileModal;
